import { Calendar, Clock, Coffee, FileText, Maximize2, Text, Timer } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { useCalendarEntries } from '../../hooks/useCalendarEntries'
import { useWorkouts } from '../../hooks/useWorkouts'
import { theme } from '../../theme/theme'
import type { CalendarEntryType } from '../../types/calendarEntry'
import type { CalendarEntry, Workout } from '../../types/database.tables'
import { getDisciplineColor } from '../../utils/disciplineColors'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'
import { Checkbox } from '../common/Checkbox'
import { DateTimeInputs } from '../common/DateTimeInputs'
import { FormField } from '../common/FormField'
import { FormIcon } from '../common/FormIcon'
import { FormInput, FormTextarea } from '../common/FormInput'
import { FormLabel } from '../common/FormLabel'
import { LargeIconButton } from '../common/LargeIconButton'
import { Modal } from '../common/Modal'
import { SportSelector, type SportType } from '../common/SportSelector'
import { TitleInput } from '../common/TitleInput'

// Activity types are now stored directly as discipline strings in the database
// No mapping needed - discipline equals activity_type

const ModalContentWrapper = styled.div`
  padding: ${theme.spacing.md};
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const FullSizeButton = styled.button<{ $disabled?: boolean }>`
  background: none;
  border: none;
  color: ${({ theme, $disabled }) => ($disabled ? theme.colors.border : theme.colors.textSecondary)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 24px;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ theme, $disabled }) => ($disabled ? 'transparent' : theme.colors.surfaceHover)};
    color: ${({ theme, $disabled }) => ($disabled ? theme.colors.border : theme.colors.text)};
  }
`

const ModalHeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const FormGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.sm} ${theme.spacing.xs};
  grid-template-columns: 40px 1fr;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const FormButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`

interface WorkoutFormModalProps {
  isOpen: boolean
  onClose: () => void
  onFullSize?: (data: { formData: any; entryType: string; initialData?: Workout; initialEntry?: CalendarEntry }) => void
  initialData?: Workout
  initialEntry?: CalendarEntry
  defaultDate?: string
  defaultEntryType?: CalendarEntryType
}

// Entry type configuration
const entryTypeConfig = {
  workout: {
    icon: Clock,
    color: theme.colors.primary,
    label: 'Workout',
  },
  rest_day: {
    icon: Coffee,
    color: getDisciplineColor('rest'),
    label: 'Rest Day',
  },
  event: {
    icon: Calendar,
    color: getDisciplineColor('event'),
    label: 'Event',
  },
  note: {
    icon: FileText,
    color: getDisciplineColor('note'),
    label: 'Note',
  },
}

const EntryTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`

export const WorkoutFormModal: React.FC<WorkoutFormModalProps> = ({
  isOpen,
  onClose,
  onFullSize,
  initialData: initialWorkout,
  initialEntry,
  defaultDate,
  defaultEntryType,
}) => {
  const { user } = useAuth()
  const [lastParsedValue, setLastParsedValue] = useState<string>('')
  const [isParsing, setIsParsing] = useState(false)
  const [entryType, setEntryType] = useState<'workout' | CalendarEntryType | undefined>(defaultEntryType)
  const [isDateTimeExpanded, setIsDateTimeExpanded] = useState(false)
  const [parseTimeout, setParseTimeout] = useState<NodeJS.Timeout | null>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const { createWorkout, updateWorkout } = useWorkouts()

  const { createEntry, updateEntry } = useCalendarEntries()

  const getInitialFormData = () => ({
    date: defaultDate || '',
    order: 0,
    activity_type: undefined as string | undefined,
    title: '',
    description: '',
    instructions: '',
    time: '',
    duration_minutes: 60,
    status: 'scheduled',
    // template_id: '' as string | undefined,
    // intervals: [] as any[] | undefined,
    // Calendar entry fields
    start_time: undefined as string | undefined,
    end_time: undefined as string | undefined,
    all_day: undefined as boolean | undefined,
  })

  const [formData, setFormData] = useState(getInitialFormData())

  const resetForm = () => {
    setFormData(getInitialFormData())
    setEntryType(entryType)
    setIsParsing(false)
    setIsDateTimeExpanded(false)
    setLastParsedValue('')
    if (parseTimeout) {
      clearTimeout(parseTimeout)
      setParseTimeout(null)
    }
  }

  useEffect(() => {
    if (initialWorkout) {
      setEntryType('workout')
      setFormData({
        ...initialWorkout,
        date: '',
        all_day: false,
        instructions: initialWorkout.instructions ?? '',
        time: initialWorkout.time ?? '',
        status: initialWorkout.status ?? 'scheduled',
        activity_type: initialWorkout.activity_type ?? 'unknown',
        // discipline: initialData.discipline,
        // template_id: initialData.template_id ?? undefined,
        // intervals: initialData.intervals ?? undefined,
        duration_minutes: 0,
        // Initialize calendar entry fields
        description: '',
        start_time: '',
        end_time: '',
      })
    } else if (initialEntry) {
      setEntryType(initialEntry.entry_type as CalendarEntryType)
      setFormData((prev) => ({
        ...prev,
        date: initialEntry.date,
        title: initialEntry.title,
        description: initialEntry.description || '',
        start_time: initialEntry.start_time || '',
        end_time: initialEntry.end_time || '',
        all_day: initialEntry.start_time === '' && initialEntry.end_time === '',
      }))
    } else if (defaultDate) {
      setFormData((prev) => ({ ...prev, date: defaultDate }))
    }

    // Set entry type from prop if provided
    if (defaultEntryType) {
      setEntryType(defaultEntryType)
      // If default entry type is event, set all_day to true
      if (defaultEntryType === 'event') {
        setFormData((prev) => ({ ...prev, all_day: true }))
      }
    }
  }, [initialWorkout, initialEntry, defaultDate, defaultEntryType])

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
      if (event.key === 'Enter' && event.metaKey) {
        handleSubmit()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
      // Focus the modal for better accessibility
      document.body.style.overflow = 'hidden'

      // Focus title input for new entries
      if (!initialWorkout && !initialEntry) {
        setTimeout(() => {
          titleInputRef.current?.focus()
        }, 100)
      }
    } else {
      // Reset form when modal closes
      if (!initialWorkout && !initialEntry) {
        resetForm()
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, initialWorkout, initialEntry])

  const triggerParseWithDelay = (value: string) => {
    if (parseTimeout) {
      clearTimeout(parseTimeout)
    }

    // Only trigger parsing if there's meaningful input
    if (!value.trim() || value.length < 3) {
      return
    }

    setParseTimeout(
      setTimeout(() => {
        parseActivityTitle(value)
      }, 250)
    )
  }

  const parseActivityTitle = async (value: string) => {
    // Only parse if the current value matches what we intended to parse
    // This prevents parsing if user continued typing
    console.log('Parsing activity title...', formData.title, value, lastParsedValue)
    if (value === lastParsedValue) {
      return
    }

    const titleValue = value.trim().toLowerCase()
    if (!titleValue) {
      return
    }

    console.log('Parsing activity title...', titleValue)
    setIsParsing(true)

    // TODO: Implement Supabase AI function call
    // For now, simulate parsing with a timeout
    try {
      await new Promise((resolve) => setTimeout(resolve, 500))

      // biome-ignore lint/suspicious/noExplicitAny: set of arbitrary fields required for Workout | CalendarEntry
      const parsedData: any = {}
      if (titleValue.toLowerCase().includes('entlastung') || titleValue.toLowerCase().includes('rest')) {
        setEntryType('rest_day')
      } else if (titleValue.includes('swim') || titleValue.includes('pool')) {
        setEntryType('workout')
        parsedData.activity_type = 'swimming'
        parsedData.duration_minutes = 60
      } else if (titleValue.includes('bike') || titleValue.includes('ride') || titleValue.includes('cycle')) {
        setEntryType('workout')
        parsedData.activity_type = 'cycling'
        parsedData.duration_minutes = 120
      } else if (titleValue.includes('run') || titleValue.includes('jog')) {
        setEntryType('workout')
        parsedData.activity_type = 'running'
        if (!formData.duration_minutes) {
          parsedData.duration_minutes = 60
        }
        if (!formData.instructions) {
          parsedData.instructions = `Start with a 5–10 min gentle warm-up jog to ease into the run, then continue for a total of 60 minutes at an easy, conversational pace (Zone 1–2). Keep your breathing relaxed, shoulders loose, and stride smooth. Finish with a 5–10 min easy jog or walk to cool down and help your body recover.`
        }
      } else if (titleValue.includes('race') && titleValue.includes('day')) {
        setEntryType('event')
      } else {
        console.log('parsed: no match')
      }

      setFormData((prev) => ({
        ...prev,
        ...parsedData,
      }))

      // Update the last parsed value to prevent re-parsing the same input
      setLastParsedValue(titleValue)
    } catch (error) {
      console.error('Failed to parse workout:', error)
    } finally {
      setIsParsing(false)
    }
  }

  const handleFullSize = () => {
    if (entryType === 'workout' && onFullSize) {
      onFullSize({
        formData,
        entryType,
        initialData: initialWorkout,
        initialEntry,
      })
    }
  }

  const showFullScreen = (e: React.FormEvent) => {
    e.preventDefault()
    handleFullSize()
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!user) return

    if (entryType === 'workout') {
      if (initialWorkout?.id) {
        await updateWorkout(initialWorkout.id, {
          user_id: user.id,
          order: formData.order,
          scheduled_date: formData.date,
          activity_type: formData.activity_type,
          time: formData.time,
          title: formData.title,
          instructions: formData.instructions,
          duration_minutes: formData.duration_minutes,
          status: formData.status,
        })
      } else {
        await createWorkout({
          user_id: user.id,
          order: formData.order,
          scheduled_date: formData.date,
          activity_type: formData.activity_type,
          time: formData.time,
          title: formData.title,
          instructions: formData.instructions,
          duration_minutes: formData.duration_minutes,
          status: formData.status,
        })
      }
    } else {
      // Prepare calendar entry data
      if (initialEntry?.id) {
        await updateEntry(initialEntry.id, {
          date: formData.date,
          entry_type: entryType as CalendarEntryType,
          title: formData.title,
          description: formData.description,
          start_time: formData.start_time,
          end_time: formData.end_time,
        })
      } else {
        await createEntry({
          user_id: user.id,
          date: formData.date ?? '',
          entry_type: entryType as CalendarEntryType,
          title: formData.title ?? '',
          description: formData.description,
          start_time: formData.start_time,
          end_time: formData.end_time,
        })
      }
    }

    // Reset form after successful submission
    if (!initialWorkout && !initialEntry) {
      resetForm()
    }

    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="900px">
      <ModalContentWrapper>
        <ModalHeader>
          <FullSizeButton
            $disabled={entryType !== 'workout' || !onFullSize}
            onClick={entryType === 'workout' && onFullSize ? handleFullSize : undefined}
            title={
              entryType === 'workout' && onFullSize ? 'Open in full view' : 'Full view only available for workouts'
            }
          >
            <Maximize2 size={16} />
          </FullSizeButton>
          <ModalTitle></ModalTitle>
          <ModalHeaderButtons>
            <ActionButton onClick={onClose}>×</ActionButton>
          </ModalHeaderButtons>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormField></FormField>
            <FormField>
              <TitleInput
                ref={titleInputRef}
                type="text"
                value={formData.title}
                onChange={(e) => {
                  const value = e.target.value
                  setFormData((prev) => ({
                    ...prev,
                    title: value,
                  }))
                  triggerParseWithDelay(value)
                }}
                onBlur={() => {
                  if (entryType === 'workout' && formData.title.trim() && formData.title !== lastParsedValue) {
                    parseActivityTitle(formData.title)
                  }
                }}
                placeholder={`Add title, e.g. 45 minute easy run`}
                showParseIndicator={isParsing}
              />
            </FormField>

            <FormField></FormField>
            <FormField>
              <EntryTypeGrid>
                {(Object.keys(entryTypeConfig) as Array<keyof typeof entryTypeConfig>).map((type) => (
                  <LargeIconButton
                    key={type}
                    selected={entryType === type}
                    color={entryTypeConfig[type].color}
                    onClick={() => setEntryType(type)}
                    icon={entryTypeConfig[type].icon}
                    label={entryTypeConfig[type].label}
                  />
                ))}
              </EntryTypeGrid>
            </FormField>

            <FormField>
              <FormIcon>
                <Clock />
              </FormIcon>
            </FormField>
            <FormField>
              <DateTimeInputs
                date={formData.date}
                time={formData.time}
                onDateChange={(date) => setFormData((prev) => ({ ...prev, date }))}
                onTimeChange={(time) => setFormData((prev) => ({ ...prev, time }))}
                showTimeInput={true}
              />
            </FormField>

            {entryType === 'workout' && (
              <>
                <FormField></FormField>
                <FormField>
                  <SportSelector
                    value={formData.activity_type as SportType}
                    onChange={(sport) => setFormData((prev) => ({ ...prev, activity_type: sport }))}
                    label=""
                  />
                </FormField>
              </>
            )}

            {entryType === 'event' && (
              <>
                <FormField></FormField>
                <FormField>
                  <Checkbox
                    id="all-day"
                    label="All day"
                    checked={formData.all_day}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        start_time: e.target.checked ? '' : prev.start_time,
                        end_time: e.target.checked ? '' : prev.end_time,
                      }))
                    }
                  />
                </FormField>

                {!formData.all_day && (
                  <>
                    <FormField></FormField>
                    <FormField>
                      <FormLabel>Start Time</FormLabel>
                      <FormInput
                        variant="borderless"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData((prev) => ({ ...prev, start_time: e.target.value }))}
                      />
                    </FormField>

                    <FormField></FormField>
                    <FormField>
                      <FormLabel>End Time</FormLabel>
                      <FormInput
                        variant="borderless"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData((prev) => ({ ...prev, end_time: e.target.value }))}
                      />
                    </FormField>
                  </>
                )}
              </>
            )}

            {entryType === 'workout' && (
              <>
                <FormField>
                  <FormIcon>
                    <Timer />
                  </FormIcon>
                </FormField>
                <FormField>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <FormInput
                    variant="borderless"
                    type="number"
                    min="1"
                    max="600"
                    value={formData.duration_minutes}
                    style={{ width: '100px' }}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))
                    }
                    placeholder="60"
                  />
                </FormField>
              </>
            )}

            <FormField>
              <FormIcon>
                <Text></Text>
              </FormIcon>
            </FormField>
            <FormField>
              <FormTextarea
                variant="borderless"
                value={entryType === 'workout' ? formData.instructions : formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    [entryType === 'workout' ? 'instructions' : 'description']: e.target.value,
                  }))
                }
                placeholder={entryType === 'workout' ? 'Add workout instructions' : 'Add description'}
              />
            </FormField>
          </FormGrid>

          <FormButtons>
            {entryType === 'workout' && onFullSize && (
              <Button type="button" onClick={showFullScreen}>
                More Options
              </Button>
            )}
            <Button type="submit" variant="primary">
              Save
            </Button>
          </FormButtons>
        </form>
      </ModalContentWrapper>
    </Modal>
  )
}
